'use client';

import React, { createContext, useContext, useCallback } from 'react';

const AssetBaseContext = createContext<string>('');

export function AssetBaseProvider({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}) {
  return <AssetBaseContext.Provider value={value}>{children}</AssetBaseContext.Provider>;
}

/** Returns a function that prepends the asset base to root-relative paths. */
export function useAssetUrl() {
  const base = useContext(AssetBaseContext);
  return useCallback(
    (path: string) => {
      if (!base || !path.startsWith('/')) return path;
      return base + path;
    },
    [base]
  );
}

/**
 * Drop-in `<img>` replacement that auto-prefixes asset base for widget mode.
 * Root-relative src (e.g. `/Christmas/BGImage.png`) gets the base prepended.
 */
export const Img = React.forwardRef<HTMLImageElement, React.ImgHTMLAttributes<HTMLImageElement>>(
  function Img({ src, ...props }, ref) {
    const resolve = useAssetUrl();
    return <img ref={ref} src={typeof src === 'string' ? resolve(src) : src} {...props} />;
  }
);

/**
 * Returns a CSS url() string with the asset base prepended.
 * Usage: `style={{ backgroundImage: bgUrl('/Hope-365/pageBG.jpg') }}`
 */
export function useBgUrl() {
  const resolve = useAssetUrl();
  return useCallback((path: string) => `url(${resolve(path)})`, [resolve]);
}
